import React, { useState } from 'react';
import { Alert, Checkbox, Input, Modal, Select, Typography, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { userMessageForError } from '../../utils/apiError';
import {
    ADMIN_API,
    buildEditPatch,
    buildReviseBody,
    describeReviseError,
    reviseHasChanges,
    topicOptions,
} from '../../utils/regulatoryAdminModel';
import {
    authorityOptions,
    breakGlassReasonValid,
    ddMmYyyyToIso,
    describeSupersedeError,
    documentTypeOptions,
    fieldFromError,
    rejectReasonValid,
} from '../../utils/regulatoryModel';
import { formatDdMmYyyy } from '../../utils/formatDdMmYyyy';

const { Text } = Typography;

/**
 * One reasoned, audited admin action (dismiss, restore, unpublish, republish, clear supersession, cancel revision).
 * Republish needs a different admin than the unpublisher (403 SEPARATION_OF_DUTIES); break-glass is offered only
 * after that refusal and only when the server says it is available.
 */
export const ReasonActionModal = ({ title, okText, description, ariaLabel, method = 'POST', path, successMessage, onClose, onDone, describeError, breakGlassAvailable = false }) => {
    const { request } = useApiRequest();
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [separation, setSeparation] = useState(false);
    const [breakGlass, setBreakGlass] = useState(false);
    const [breakGlassReason, setBreakGlassReason] = useState('');

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            const body = { reason: reason.trim() };
            if (breakGlass) {
                body.breakGlass = true;
                body.breakGlassReason = breakGlassReason.trim();
            }
            await request(method, path, { body });
            message.success(successMessage);
            onDone();
        } catch (err) {
            const code = err?.details?.code || err?.code;
            if (err?.status === 403 && (code === 'SEPARATION_OF_DUTIES' || code === 'BREAK_GLASS_DISABLED' || code === 'BREAK_GLASS_REFUSED')) {
                setSeparation(true);
                setError(code === 'SEPARATION_OF_DUTIES' ? 'A different admin must do this, because you made the previous change.' : 'A different admin must do this; break-glass is not enabled.');
            } else {
                setError(describeError ? describeError(err) : userMessageForError(err));
            }
        } finally {
            setBusy(false);
        }
    };
    const bgBlocked = breakGlass && !breakGlassReasonValid(breakGlassReason);

    return (
        <Modal open title={title} okText={okText} onOk={submit} onCancel={onClose} okButtonProps={{ disabled: !rejectReasonValid(reason) || bgBlocked || busy }} confirmLoading={busy}>
            {description && <div><Text type="secondary">{description}</Text></div>}
            <Input.TextArea aria-label={ariaLabel} rows={3} style={{ marginTop: 8 }} placeholder="Why? (5 to 1000 characters; recorded in the audit log)" value={reason} onChange={(e) => setReason(e.target.value)} />
            {error && <Alert type="error" style={{ marginTop: 8 }} message={error} data-testid="reason-modal-error" />}
            {separation && breakGlassAvailable && (
                <div style={{ marginTop: 8 }} data-testid="break-glass-reason-action">
                    <Checkbox checked={breakGlass} onChange={(e) => setBreakGlass(e.target.checked)}>Proceed under break-glass (only because this deployment has a single platform admin; audited).</Checkbox>
                    {breakGlass && (
                        <Input.TextArea aria-label="Break-glass reason" rows={2} placeholder="Why can a second admin not do this? (at least 15 characters)" value={breakGlassReason} onChange={(e) => setBreakGlassReason(e.target.value)} />
                    )}
                </div>
            )}
        </Modal>
    );
};

/**
 * Supersede: `item` is the OLD document; the admin picks the newer one that replaces it. Only PUBLISHED
 * documents of the same authority are offered; the `initialReplacement` (from a draft's suggestion) is
 * pre-selected and flagged when it is not yet published (the server then refuses with REPLACEMENT_NOT_PUBLISHED).
 */
export const SupersedeModal = ({ item, initialReplacement, onClose, onDone }) => {
    const { request } = useApiRequest();
    const [replacement, setReplacement] = useState(initialReplacement?.id);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const candidates = useResource(
        async () => (await request('GET', '/regulatory-content', { query: { authority: item.authorityName, status: 'published', limit: 100 } })).data.items || [],
        [request, item.id, item.authorityName],
    );
    const label = (c, extra = '') => `${c.title}${c.officialDocumentNumber ? ` (${c.officialDocumentNumber})` : ''}${extra}`;
    const options = (candidates.data || []).filter((c) => c.id !== item.id).map((c) => ({ value: c.id, label: label(c) }));
    if (initialReplacement && !options.some((o) => o.value === initialReplacement.id)) {
        options.unshift({ value: initialReplacement.id, label: label(initialReplacement, ' - awaiting approval') });
    }

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await request('PATCH', `/regulatory-content/${item.id}/supersede`, { body: { supersededById: replacement, reason: reason.trim() } });
            message.success('Marked as superseded.');
            onDone();
        } catch (err) {
            setError(describeSupersedeError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal open title="Mark as superseded" okText="Mark superseded" onOk={submit} onCancel={onClose} okButtonProps={{ disabled: !replacement || !rejectReasonValid(reason) || busy }} confirmLoading={busy}>
            <Text>{item.title}</Text>
            <div style={{ margin: '8px 0' }}>
                <Text type="secondary">Replaced by (published, same authority)</Text>
                {candidates.status === 'error' ? (
                    <Alert type="error" message={userMessageForError(candidates.error)} />
                ) : (
                    <>
                        {candidates.status === 'ready' && options.length === 0 && (
                            <Alert type="warning" data-testid="no-published-replacement" message="No published replacement exists yet. Approve the newer document first, then mark this one superseded." />
                        )}
                        <Select aria-label="Replacing document" style={{ width: '100%' }} placeholder="Choose the newer document" value={replacement} onChange={setReplacement} options={options} showSearch optionFilterProp="label" />
                    </>
                )}
            </div>
            <Input.TextArea aria-label="Supersession reason" rows={3} placeholder="Why is it superseded? (5 to 1000 characters)" value={reason} onChange={(e) => setReason(e.target.value)} />
            {error && <Alert type="error" style={{ marginTop: 8 }} message={error} data-testid="supersede-error" />}
        </Modal>
    );
};

/**
 * Edit before any approval (Commandment VII freezes an approved version). RBI title, text, reference and link are
 * owned by the feed; SEBI's title is refreshed from the feed until the item is approved.
 */
export const EditModal = ({ item, sources, onClose, onDone }) => {
    const { request } = useApiRequest();
    const feedOwned = item.sourceName === 'rbi';
    const [form, setForm] = useState({
        title: item.title,
        text: item.text || '',
        officialDocumentNumber: item.officialDocumentNumber || '',
        sourceUrl: item.sourceUrl || '',
        effectiveDate: item.effectiveDate ? formatDdMmYyyy(item.effectiveDate) : '',
        jurisdictionScope: (item.jurisdictionScope || []).join(', '),
        industryApplicability: item.industryApplicability || [],
        relatedStandardId: item.relatedStandardId || undefined,
        topicCodes: (item.topics || []).map((t) => t.id),
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [badField, setBadField] = useState(null);
    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
    const setEv = (k) => (e) => set(k)(e.target.value);

    const effBad = form.effectiveDate.trim() !== '' && !ddMmYyyyToIso(form.effectiveDate);
    const body = buildEditPatch(item, form, { ddMmYyyyToIso });
    const changed = Object.keys(body).length > 0;
    const standards = sources?.bridgeableStandards?.[item.authorityName] || [];
    const standardOptions = [...new Set([...standards, ...(item.relatedStandardId ? [item.relatedStandardId] : [])])].map((s) => ({ value: s, label: s }));
    const industryOptions = (sources?.industryApplicabilityCodes || []).map((c) => ({ value: c, label: c }));
    const bad = (f) => (badField === f ? 'true' : undefined);

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await request('PATCH', `/regulatory-content/${item.id}`, { body });
            message.success('Saved.');
            onDone();
        } catch (err) {
            setError(userMessageForError(err));
            setBadField(fieldFromError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal open title="Edit document" okText="Save" onOk={submit} onCancel={onClose} okButtonProps={{ disabled: !changed || !form.title.trim() || effBad || busy }} confirmLoading={busy}>
            <Input aria-label="Title" status={badField === 'title' ? 'error' : undefined} aria-invalid={bad('title')} disabled={feedOwned} value={form.title} onChange={setEv('title')} />
            {feedOwned && <Text type="secondary" style={{ fontSize: 12 }} data-testid="title-note">The title comes from the RBI feed</Text>}
            {item.sourceName === 'sebi' && <Text type="secondary" style={{ fontSize: 12 }} data-testid="title-note">Title is refreshed from the feed until approved</Text>}
            <div style={{ marginTop: 8 }}>
                <Input aria-label="Reference number" status={badField === 'officialDocumentNumber' ? 'error' : undefined} aria-invalid={bad('officialDocumentNumber')} disabled={feedOwned} placeholder="Reference number" value={form.officialDocumentNumber} onChange={setEv('officialDocumentNumber')} />
            </div>
            <div style={{ marginTop: 8 }}>
                <Input aria-label="Official source link" disabled={feedOwned} placeholder="https:// link on the regulator's own site" value={form.sourceUrl} onChange={setEv('sourceUrl')} />
            </div>
            <div style={{ marginTop: 8 }}>
                <Input aria-label="Effective date" placeholder="Effective date (DD-MM-YYYY), empty for none" value={form.effectiveDate} onChange={setEv('effectiveDate')} />
                {effBad && <Text type="danger" style={{ fontSize: 12 }}>Enter the date as DD-MM-YYYY.</Text>}
            </div>
            <div style={{ marginTop: 8 }}>
                <Input aria-label="Jurisdiction scope" placeholder="Jurisdiction scope, comma separated (e.g. India, Maharashtra)" value={form.jurisdictionScope} onChange={setEv('jurisdictionScope')} />
            </div>
            <div style={{ marginTop: 8 }}>
                <Select aria-label="Industry applicability" mode="multiple" style={{ width: '100%' }} placeholder="Industries it applies to" value={form.industryApplicability} onChange={(v) => set('industryApplicability')(v || [])} options={industryOptions} />
            </div>
            <div style={{ marginTop: 8 }}>
                <Select aria-label="Topics" mode="multiple" style={{ width: '100%' }} placeholder="Topics (from the taxonomy)" optionFilterProp="label" showSearch value={form.topicCodes} onChange={(v) => set('topicCodes')(v || [])} options={topicOptions(sources?.taxonomy)} />
            </div>
            <div style={{ marginTop: 8 }}>
                <Select aria-label="Related standard" allowClear style={{ width: '100%' }} placeholder="Related standard (none)" value={form.relatedStandardId} onChange={(v) => set('relatedStandardId')(v || undefined)} options={standardOptions} />
            </div>
            <div style={{ marginTop: 8 }}>
                <Input.TextArea aria-label="Official text" aria-invalid={bad('text')} rows={10} value={form.text} disabled={feedOwned} onChange={setEv('text')} />
                {feedOwned && <Text type="secondary" style={{ fontSize: 12 }}>The text of RBI documents is maintained by the feed and cannot be edited here.</Text>}
            </div>
            {error && <Alert type="error" style={{ marginTop: 8 }} message={error} />}
        </Modal>
    );
};

/**
 * Revise an APPROVED item. It records a pending revision; the published version stays live until a different admin
 * approves a draft made from it (Commandment VII). Only changed fields are sent.
 */
export const ReviseModal = ({ item, sources, onClose, onDone }) => {
    const { request } = useApiRequest();
    const [form, setForm] = useState({
        reason: '',
        title: item.title || '',
        text: item.text || '',
        officialDocumentNumber: item.officialDocumentNumber || '',
        authorityName: item.authorityName,
        documentType: item.documentType,
        sourceUrl: item.sourceUrl || '',
        publishedDate: formatDdMmYyyy(item.publishedAt),
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [badField, setBadField] = useState(null);
    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
    const setEv = (k) => (e) => set(k)(e.target.value);

    const dateBad = form.publishedDate.trim() !== '' && !ddMmYyyyToIso(form.publishedDate);
    const body = buildReviseBody(item, form, { ddMmYyyyToIso, formatDdMmYyyy });
    const ok = reviseHasChanges(body) && rejectReasonValid(form.reason) && !dateBad && !busy;

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await request('POST', ADMIN_API.revise(item.id), { body });
            message.success('Revision recorded. Draft it again; the published version stays live until a different admin approves.');
            onDone();
        } catch (err) {
            setError(describeReviseError(err));
            setBadField(fieldFromError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal open title="Revise source" okText="Record revision" onOk={submit} onCancel={onClose} okButtonProps={{ disabled: !ok }} confirmLoading={busy}>
            <Text type="secondary">The published version stays live until a different admin approves a new draft made from this revision.</Text>
            <div style={{ marginTop: 8 }}>
                <Input aria-label="Revision reason" status={badField === 'reason' ? 'error' : undefined} placeholder="What changed in the source? (5 to 1000 characters)" value={form.reason} onChange={setEv('reason')} />
            </div>
            <div style={{ marginTop: 8 }}><Input aria-label="Title" status={badField === 'title' ? 'error' : undefined} value={form.title} onChange={setEv('title')} /></div>
            <div style={{ marginTop: 8 }}><Input aria-label="Reference number" status={badField === 'officialDocumentNumber' ? 'error' : undefined} value={form.officialDocumentNumber} onChange={setEv('officialDocumentNumber')} /></div>
            <div style={{ marginTop: 8 }}><Select aria-label="Authority" style={{ width: '100%' }} value={form.authorityName} onChange={set('authorityName')} options={authorityOptions(sources?.authorities)} /></div>
            <div style={{ marginTop: 8 }}><Select aria-label="Document type" style={{ width: '100%' }} value={form.documentType} onChange={set('documentType')} options={documentTypeOptions(sources?.documentTypes)} /></div>
            <div style={{ marginTop: 8 }}><Input aria-label="Official source link" value={form.sourceUrl} onChange={setEv('sourceUrl')} /></div>
            <div style={{ marginTop: 8 }}>
                <Input aria-label="Issue date" placeholder="Issue date (DD-MM-YYYY)" value={form.publishedDate} onChange={setEv('publishedDate')} />
                {dateBad && <Text type="danger" style={{ fontSize: 12 }}>Enter the date as DD-MM-YYYY.</Text>}
            </div>
            <div style={{ marginTop: 8 }}><Input.TextArea aria-label="Revised official text" rows={8} value={form.text} onChange={setEv('text')} /></div>
            {error && <Alert type="error" style={{ marginTop: 8 }} message={error} data-testid="revise-error" />}
        </Modal>
    );
};
